// Validation utility functions for real-time form validation

/**
 * Debounce function to limit API calls
 */
export const debounce = (func, wait = 500) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  if (!email) return { valid: false, message: 'Email is required' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Please enter a valid email address' };
  }
  return { valid: true, message: 'Email format is valid' };
};

/**
 * Calculate password strength
 */
export const calculatePasswordStrength = (password) => {
  if (!password) {
    return {
      strength: 0,
      label: '',
      score: 0,
      feedback: [],
      isValid: false
    };
  }

  let strength = 0;
  const feedback = [];

  // Length check
  if (password.length >= 8) {
    strength += 20;
  } else {
    feedback.push('At least 8 characters');
  }

  // Uppercase check
  if (/[A-Z]/.test(password)) {
    strength += 20;
  } else {
    feedback.push('One uppercase letter');
  }

  // Lowercase check
  if (/[a-z]/.test(password)) {
    strength += 20;
  } else {
    feedback.push('One lowercase letter');
  }

  // Number check
  if (/\d/.test(password)) {
    strength += 20;
  } else {
    feedback.push('One number');
  }

  // Special character check
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    strength += 20;
  } else {
    feedback.push('One special character');
  }

  // Sequential pattern check (simple)
  if (/(123|abc|ABC|321|cba|CBA)/i.test(password)) {
    strength = Math.max(0, strength - 10);
  }

  // Repeated characters
  if (/(.)\1{2,}/.test(password)) {
    strength = Math.max(0, strength - 10);
  }

  let label = '';
  if (strength >= 80) label = 'Very Strong';
  else if (strength >= 60) label = 'Strong';
  else if (strength >= 40) label = 'Medium';
  else if (strength >= 20) label = 'Weak';
  else label = 'Very Weak';

  return {
    strength: Math.min(100, strength),
    label,
    score: strength,
    feedback: feedback.length === 0 ? ['Strong password!'] : feedback,
    isValid: strength >= 60 && password.length >= 8
  };
};

/**
 * Validate username format
 */
export const validateUsername = (username) => {
  if (!username) return { valid: false, message: 'Username is required' };
  if (username.length < 3) {
    return { valid: false, message: 'Username must be at least 3 characters' };
  }
  if (username.length > 30) {
    return { valid: false, message: 'Username must be less than 30 characters' };
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
    return { valid: false, message: 'Username can only contain letters, numbers, underscores, and hyphens' };
  }
  return { valid: true, message: 'Username format is valid' };
};

/**
 * Validate site code format
 */
export const validateSiteCode = (siteCode) => {
  if (!siteCode) return { valid: false, message: 'Site code is required' };
  if (!/^[A-Z0-9]+$/.test(siteCode)) {
    return { valid: false, message: 'Site code must contain only uppercase letters and numbers' };
  }
  if (siteCode.length < 2) {
    return { valid: false, message: 'Site code must be at least 2 characters' };
  }
  if (siteCode.length > 50) {
    return { valid: false, message: 'Site code must be less than 50 characters' };
  }
  return { valid: true, message: 'Site code format is valid' };
};

/**
 * Validate phone number (basic)
 */
export const validatePhone = (phone) => {
  if (!phone) return { valid: true, message: '' }; // Phone is optional
  const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  if (!phoneRegex.test(phone)) {
    return { valid: false, message: 'Please enter a valid phone number' };
  }
  return { valid: true, message: 'Phone number is valid' };
};

/**
 * Validate numeric input (for capacity)
 */
export const validateNumber = (value, min = 0, max = null) => {
  if (!value && value !== 0) return { valid: false, message: 'This field is required' };
  const num = parseFloat(value);
  if (isNaN(num)) {
    return { valid: false, message: 'Please enter a valid number' };
  }
  if (num < min) {
    return { valid: false, message: `Value must be at least ${min}` };
  }
  if (max !== null && num > max) {
    return { valid: false, message: `Value must be less than ${max}` };
  }
  return { valid: true, message: 'Number is valid' };
};

/**
 * Get field validation status class
 */
export const getFieldStatusClass = (isValid, isChecking, hasValue) => {
  if (isChecking) return 'border-blue-500';
  if (hasValue) {
    return isValid ? 'border-green-500' : 'border-red-500';
  }
  return 'border-gray-300';
};

/**
 * Get validation icon
 */
export const getValidationIcon = (isValid, isChecking, hasValue) => {
  if (isChecking) return 'spinner';
  if (hasValue) {
    return isValid ? 'check' : 'x';
  }
  return null;
};

